import { useState, useEffect } from "react";
import { X, Upload, FileUp, Save, Table2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { MODULES } from "@/lib/modules";
import ImportMiniMap from "@/components/ImportMiniMap";
import { useI18n } from "@/context/I18nContext";

const _norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]/gi, "");

function splitCsvLine(line, delim) {
  return line.split(delim).map((s) => s.trim().replace(/^"|"$/g, ""));
}

function parseColumns(fmtKey, text) {
  try {
    if (fmtKey === "geojson") {
      const j = JSON.parse(text);
      const f0 = (j.features || [])[0];
      return f0 && f0.properties ? Object.keys(f0.properties) : [];
    }
    if (fmtKey === "csv" || fmtKey === "teryt") {
      const line = text.split(/\r?\n/).find((l) => l.trim()) || "";
      const delim = line.includes(";") ? ";" : line.includes("\t") ? "\t" : ",";
      return splitCsvLine(line, delim).filter(Boolean);
    }
  } catch (e) { /* ignore parse errors */ }
  return [];
}

// Return up to `n` preview rows as objects keyed by column name
function parseRows(fmtKey, text, cols, n = 4) {
  const out = [];
  try {
    if (fmtKey === "geojson") {
      const j = JSON.parse(text);
      for (const f of (j.features || []).slice(0, n)) {
        out.push(f.properties || {});
      }
    } else if (fmtKey === "csv" || fmtKey === "teryt") {
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length) {
        const delim = lines[0].includes(";") ? ";" : lines[0].includes("\t") ? "\t" : ",";
        for (const ln of lines.slice(1, n + 1)) {
          const vals = splitCsvLine(ln, delim);
          const row = {};
          cols.forEach((c, i) => { row[c] = vals[i] ?? ""; });
          out.push(row);
        }
      }
    }
  } catch (e) { /* ignore */ }
  return out;
}

const FORMATS = [
  { key: "geojson", label: "GeoJSON", accept: ".geojson,.json", binary: false },
  { key: "kml", label: "KML/KMZ", accept: ".kml", binary: false },
  { key: "csv", label: "CSV (współrzędne)", accept: ".csv", binary: false },
  { key: "teryt", label: "TERYT / SIMC / ULIC (CSV)", accept: ".csv", binary: false },
  { key: "dxf", label: "DXF (AutoCAD)", accept: ".dxf", binary: false },
  { key: "shp", label: "Shapefile (.zip)", accept: ".zip", binary: true },
];

const SUGGEST = { point: "poles", line: "ducts", polygon: "zones" };
const TARGET_LABELS = { layer: "Warstwa", poles: "Słupy", wells: "Studnie", clients: "Abonenci", closures: "Mufy", splitters: "Splittery", odf: "Szafy", olt: "OLT", onu: "ONU / ONT", switches: "Switche", routes: "Trasy kablowe", ducts: "Kanalizacja", microducts: "Mikrokanaliz.", pipelines: "Rurociągi", zones: "Strefy" };
const TARGET_GROUPS = [
  ["Punkty", ["poles", "wells", "clients", "closures", "splitters", "odf", "olt", "onu", "switches"]],
  ["Linie", ["routes", "ducts", "microducts", "pipelines"]],
  ["Poligony", ["zones"]],
];
const TargetOptions = ({ t, short }) => (
  <>
    <option value="layer">{short ? t("Warstwa (własna nazwa)") : t("Warstwa podkładowa (własna nazwa, np. Planowane trasy)")}</option>
    {TARGET_GROUPS.map(([g, keys]) => (
      <optgroup key={g} label={t(g)}>
        {keys.map((k) => <option key={k} value={k}>{t(TARGET_LABELS[k])}</option>)}
      </optgroup>
    ))}
  </>
);

const EPSG_OPTIONS = [
  { v: 4326, l: "EPSG:4326 – WGS84 (GPS)" },
  { v: 2180, l: "EPSG:2180 – PUWG 1992 (Polska)" },
  { v: 2176, l: "EPSG:2176 – PUWG 2000 strefa 5" },
  { v: 2177, l: "EPSG:2177 – PUWG 2000 strefa 6" },
  { v: 2178, l: "EPSG:2178 – PUWG 2000 strefa 7" },
  { v: 2179, l: "EPSG:2179 – PUWG 2000 strefa 8" },
];

export default function ImportDialog({ onClose, onImported }) {
  const { t } = useI18n();
  const [layerName, setLayerName] = useState("");
  const [format, setFormat] = useState("geojson");
  const [target, setTarget] = useState("layer");
  const [epsg, setEpsg] = useState(2180);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [columns, setColumns] = useState([]);
  const [fieldMap, setFieldMap] = useState({});
  const [rows, setRows] = useState([]);
  const [tplBusy, setTplBusy] = useState(false);
  const [shapefiles, setShapefiles] = useState([]);
  const [shpName, setShpName] = useState("");
  const [batch, setBatch] = useState({});
  const [sampleGeo, setSampleGeo] = useState([]);
  const [suggestedEpsg, setSuggestedEpsg] = useState(null);
  const [nameField, setNameField] = useState("");
  const [descField, setDescField] = useState("");
  const [customMaps, setCustomMaps] = useState([]); // [{field, col}] — dowolne kolumny → własne pola

  const fmt = FORMATS.find((f) => f.key === format);
  const needsEpsg = format === "dxf" || format === "shp";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLayerName(file.name.replace(/\.[^.]+$/, ""));
    const f = FORMATS.find((x) => file.name.toLowerCase().endsWith(x.accept.split(",")[0].replace("*", "")));
    if (f && !["teryt"].includes(format)) setFormat(f.key);
    if (fmt?.binary || file.name.toLowerCase().endsWith(".zip")) {
      try {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
        setContent(b64);
        setColumns([]); setRows([]);
      } catch (err) {
        toast.error(t("Nie udało się odczytać pliku"));
      }
    } else {
      const text = await file.text();
      setContent(text);
      const fk = f ? f.key : format;
      const cols = parseColumns(fk, text);
      setColumns(cols);
      setRows(parseRows(fk, text, cols));
    }
  };

  // Recompute columns/preview when the format changes (non-binary content already loaded)
  useEffect(() => {
    if (content && !fmt?.binary) {
      const cols = parseColumns(format, content);
      setColumns(cols);
      setRows(parseRows(format, content, cols));
    }
  }, [format]); // eslint-disable-line react-hooks/exhaustive-deps

  // Geo preview (mini-map + EPSG detection) — backend inspect for Shapefile & DXF
  useEffect(() => {
    if (!["shp", "dxf"].includes(format) || !content) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/import/inspect", { layer_name: layerName || "Warstwa", format, content, target: "layer", epsg: Number(epsg) || null, shp_name: shpName || null });
        if (cancelled) return;
        if (format === "shp") {
          setColumns(data.columns || []);
          setRows(data.rows || []);
          setShapefiles(data.shapefiles || []);
          if (data.selected && !shpName) setShpName(data.selected);
          if (data.geom_type && SUGGEST[data.geom_type] && target === "layer") setTarget(SUGGEST[data.geom_type]);
          if (data.detected_epsg) setEpsg(data.detected_epsg);
        }
        setSampleGeo(data.sample_geo || []);
        setSuggestedEpsg(data.suggested_epsg || null);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    })();
    return () => { cancelled = true; };
  }, [format, content, shpName, epsg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((format === "shp" || format === "dxf") && shapefiles.length > 1) {
      setBatch(Object.fromEntries(shapefiles.map((s) => [s.name, { include: true, target: SUGGEST[s.geom_type] || "layer" }])));
    }
  }, [shapefiles, format]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLayerCols = async (name) => {
    try {
      const { data } = await api.post("/import/inspect", { layer_name: name, format: "shp", content, target: "layer", epsg: Number(epsg) || null, shp_name: name });
      return data.columns || [];
    } catch { return []; }
  };
  const autoMap = (tgt, cols) => {
    const m = {};
    (MODULES[tgt]?.fields || []).forEach((fl) => {
      if (fl.name === "name" || fl.name === "number") return;
      const hit = cols.find((c) => _norm(c) === _norm(fl.name) || _norm(c) === _norm(fl.label));
      if (hit) m[fl.name] = hit;
    });
    return m;
  };
  const toggleExpand = async (name) => {
    const cur = batch[name] || {};
    if (cur.expanded) { setBatch((b) => ({ ...b, [name]: { ...b[name], expanded: false } })); return; }
    let cols = cur.columns;
    if (!cols) cols = await loadLayerCols(name);
    const tgt = cur.target || "layer";
    const fm = cur.fieldMap || (tgt !== "layer" ? autoMap(tgt, cols) : {});
    setBatch((b) => ({ ...b, [name]: { ...b[name], expanded: true, columns: cols, fieldMap: fm } }));
  };

  const doImportAll = async () => {
    const items = shapefiles.filter((s) => batch[s.name]?.include);
    if (!items.length) return toast.error(t("Zaznacz przynajmniej jedną warstwę"));
    setBusy(true);
    let total = 0;
    try {
      for (const s of items) {
        const row = batch[s.name] || {};
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post("/import/gis", {
          layer_name: s.name.replace(/\.shp$/, ""), format, content,
          target: row.target, epsg: needsEpsg ? Number(epsg) : null, shp_name: s.name,
          field_map: row.target !== "layer" ? (row.fieldMap || null) : null,
          name_field: row.target === "layer" ? (nameField || null) : null,
          desc_field: row.target === "layer" ? (descField || null) : null,
        });
        total += data.imported || 0;
      }
      toast.success(`${t("Zaimportowano warstwy")}: ${items.length} · ${t("obiektów")}: ${total}`);
      onImported(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Load saved template for the target, else auto-map source columns to module fields
  useEffect(() => {
    if (target === "layer" || !columns.length) return;
    let cancelled = false;
    (async () => {
      const flds = MODULES[target]?.fields || [];
      let saved = {};
      try {
        const { data } = await api.get(`/import/templates/${target}`);
        saved = data?.field_map || {};
      } catch { /* ignore */ }
      if (cancelled) return;
      const map = {};
      flds.forEach((fl) => {
        // saved template wins if its column still exists in the file
        if (saved[fl.name] && columns.includes(saved[fl.name])) { map[fl.name] = saved[fl.name]; return; }
        const hit = columns.find((c) => _norm(c) === _norm(fl.name) || _norm(c) === _norm(fl.label));
        if (hit) map[fl.name] = hit;
      });
      setFieldMap(map);
      const nativeNames = flds.map((fl) => fl.name);
      const custom = Object.entries(saved)
        .filter(([k, v]) => v && !nativeNames.includes(k) && columns.includes(v))
        .map(([k, v]) => ({ field: k, col: v }));
      setCustomMaps(custom);
    })();
    return () => { cancelled = true; };
  }, [target, columns]);

  const _sanitizeField = (s) => String(s || "").trim().replace(/[.$]/g, "_");
  const addCustomRow = () => setCustomMaps((a) => [...a, { field: "", col: "" }]);
  const updCustom = (i, patch) => setCustomMaps((a) => a.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const rmCustom = (i) => setCustomMaps((a) => a.filter((_, idx) => idx !== i));
  const addAllColumns = () => {
    const used = new Set(Object.values(fieldMap).filter(Boolean));
    setCustomMaps(columns.filter((c) => !used.has(c)).map((c) => ({ field: _sanitizeField(c), col: c })));
  };

  const saveTemplate = async () => {
    setTplBusy(true);
    try {
      const extra = Object.fromEntries(customMaps.filter((m) => _sanitizeField(m.field) && m.col).map((m) => [_sanitizeField(m.field), m.col]));
      const clean = Object.fromEntries(Object.entries({ ...fieldMap, ...extra }).filter(([, v]) => v));
      await api.post(`/import/templates/${target}`, { field_map: clean });
      toast.success(t("Zapisano szablon mapowania"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setTplBusy(false); }
  };

  const doImport = async () => {
    if (!content) return toast.error(t("Wybierz plik"));
    setBusy(true);
    const extraMap = Object.fromEntries(
      customMaps.filter((m) => _sanitizeField(m.field) && m.col).map((m) => [_sanitizeField(m.field), m.col])
    );
    const mergedMap = { ...fieldMap, ...extraMap };
    try {
      const { data } = await api.post("/import/gis", {
        layer_name: layerName || "Warstwa", format, content, target,
        epsg: needsEpsg ? Number(epsg) : null,
        shp_name: format === "shp" ? (shpName || null) : null,
        field_map: target !== "layer" ? mergedMap : null,
        name_field: target === "layer" ? (nameField || null) : null,
        desc_field: target === "layer" ? (descField || null) : null,
      });
      toast.success(`${t("Zaimportowano")} ${data.imported} ${t("obiektów")}`);
      onImported();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const previewCols = columns.slice(0, 8);
  const inPl = (p) => p.lat >= 49 && p.lat <= 54.9 && p.lng >= 14 && p.lng <= 24.3;
  const outOfPoland = sampleGeo.length > 0 && sampleGeo.filter((p) => !inPl(p)).length > sampleGeo.length / 2;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4" data-testid="import-dialog">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Upload size={18} /> {t("Import GIS / QGIS / CAD")}</h3>
          <button onClick={onClose} data-testid="import-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t("Format źródła")}</label>
            <select value={format} onChange={(e) => { setFormat(e.target.value); setContent(""); setColumns([]); setRows([]); setShapefiles([]); setShpName(""); setSampleGeo([]); setSuggestedEpsg(null); }} data-testid="import-format"
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm">
              {FORMATS.map((f) => <option key={f.key} value={f.key}>{t(f.label)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t("Plik warstwy")}</label>
            <label className="flex items-center gap-2 bg-slate-900 border border-dashed border-slate-600 rounded px-3 py-3 cursor-pointer hover:border-cyan-500">
              <FileUp size={16} className="text-cyan-400" />
              <span className="text-sm text-slate-300">{content ? `${layerName} (${format})` : `${t("Wybierz plik")} ${fmt?.accept}`}</span>
              <input type="file" accept={fmt?.accept} className="hidden" onChange={onFile} data-testid="import-file-input" />
            </label>
          </div>
          {needsEpsg && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t("Układ współrzędnych źródła (EPSG)")}</label>
              <select value={epsg} onChange={(e) => setEpsg(e.target.value)} data-testid="import-epsg"
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm">
                {EPSG_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">{t("Dane z QGIS/AutoCAD w Polsce zwykle w EPSG:2180 — zostaną przeliczone na WGS84.")}</p>
            </div>
          )}
          {sampleGeo.length > 0 && (
            <div data-testid="import-minimap-wrap">
              <label className="text-xs text-slate-400 mb-1 block">{t("Podgląd położenia (pierwsze punkty) — sprawdź, czy leżą we właściwym miejscu")}</label>
              <ImportMiniMap points={sampleGeo} />
              {outOfPoland ? (
                <div data-testid="import-outside-poland" className="mt-1.5 rounded border border-red-600/60 bg-red-950/40 px-3 py-2 text-xs text-red-200 flex items-start gap-2">
                  <span className="text-red-400 text-sm leading-none mt-0.5">⚠</span>
                  <div className="flex-1">
                    <div className="font-semibold">{t("Punkty leżą poza Polską — prawdopodobnie zły układ współrzędnych.")}</div>
                    {suggestedEpsg && Number(suggestedEpsg) !== Number(epsg) && (
                      <button onClick={() => setEpsg(suggestedEpsg)} data-testid="apply-suggested-epsg"
                        className="mt-1 px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-[11px]">
                        {t("Zastosuj sugerowany")} EPSG:{suggestedEpsg}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-amber-300/80 mt-1">{t("Jeśli punkty są w złym miejscu, zmień układ współrzędnych (EPSG) powyżej.")}</p>
              )}
            </div>
          )}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t("Nazwa warstwy")}</label>
            <input value={layerName} onChange={(e) => setLayerName(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm" />
          </div>
          {(format === "geojson" || format === "kml" || format === "csv" || format === "shp") && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">{t("Importuj jako")}</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="import-target"
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm">
                <TargetOptions t={t} />
              </select>
              <p className="text-[11px] text-slate-500 mt-1">{t("Wybierz, gdzie trafią obiekty: punkty do słupów, studni, abonentów lub urządzeń, linie do tras kablowych, kanalizacji lub mikrokanalizacji. Warstwa podkładowa dostaje nazwę z pola wyżej.")}</p>
            </div>
          )}
          {target === "layer" && columns.length > 0 && (
            <div data-testid="import-layer-fields" className="border-t border-slate-700 pt-3 space-y-2">
              <label className="text-xs text-slate-400 block">{t("Kolumny warstwy → etykieta na mapie")}</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-300 w-24 shrink-0">{t("Nazwa")}</span>
                <select value={nameField} onChange={(e) => setNameField(e.target.value)} data-testid="layer-name-field"
                  className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs">
                  <option value="">— auto (name/nazwa) —</option>
                  {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-300 w-24 shrink-0">{t("Opis")}</span>
                <select value={descField} onChange={(e) => setDescField(e.target.value)} data-testid="layer-desc-field"
                  className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs">
                  <option value="">— {t("brak")} —</option>
                  {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-slate-500">{t("Wybrane kolumny pokażą się w dymku obiektu warstwy na mapie.")}</p>
            </div>
          )}
          {(format === "shp" || format === "dxf") && shapefiles.length > 1 && (
            <div data-testid="shp-picker" className="space-y-2">
              <label className="text-xs text-slate-400 block">{format === "dxf" ? t("Plik DXF zawiera kilka warstw") : t("Archiwum zawiera kilka warstw .shp")}</label>
              <select value={shpName} onChange={(e) => { setShpName(e.target.value); setColumns([]); setRows([]); setFieldMap({}); }} data-testid="shp-select"
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm">
                {shapefiles.map((s) => <option key={s.name} value={s.name}>{s.name} — {s.geom_type} ({s.count})</option>)}
              </select>
              <div className="rounded border border-slate-700 bg-slate-900/60 p-2 space-y-1" data-testid="shp-batch">
                <div className="text-[11px] text-slate-400 mb-1">{t("Import wielu warstw naraz — zaznacz i wskaż cel dla każdej:")}</div>
                {shapefiles.map((s) => {
                  const autoKey = SUGGEST[s.geom_type] || "layer";
                  const cur = batch[s.name]?.target || "layer";
                  return (
                  <div key={s.name}>
                  <div className="flex items-center gap-2 flex-wrap" data-testid={`batch-row-${s.name}`}>
                    <input type="checkbox" checked={!!batch[s.name]?.include} data-testid={`batch-inc-${s.name}`}
                      onChange={(e) => setBatch((b) => ({ ...b, [s.name]: { ...(b[s.name] || {}), include: e.target.checked } }))} className="accent-cyan-500" />
                    <span className="text-xs text-slate-300 flex-1 truncate min-w-[120px]">{s.name} <span className="text-slate-500">({s.geom_type} · {s.count})</span></span>
                    <span data-testid={`batch-auto-${s.name}`}
                      className={`text-[10px] px-1.5 py-0.5 rounded ${cur === autoKey ? "bg-cyan-900/40 text-cyan-300" : "bg-amber-900/40 text-amber-300"}`}>
                      {cur === autoKey ? `auto: ${t(TARGET_LABELS[autoKey])}` : `${t("zmieniono")} → ${t(TARGET_LABELS[cur])}`}
                    </span>
                    <select value={cur} data-testid={`batch-target-${s.name}`}
                      onChange={(e) => { const v = e.target.value; setBatch((b) => { const row = { ...(b[s.name] || {}), target: v }; if (row.columns && v !== "layer") row.fieldMap = autoMap(v, row.columns); return { ...b, [s.name]: row }; }); }}
                      className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-xs">
                      <TargetOptions t={t} short />
                    </select>
                    {cur !== "layer" && (
                      <button onClick={() => toggleExpand(s.name)} data-testid={`batch-fields-${s.name}`}
                        className="text-[11px] px-1.5 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-700">
                        {batch[s.name]?.expanded ? t("Ukryj pola") : t("Pola ▸")}
                      </button>
                    )}
                  </div>
                  {batch[s.name]?.expanded && cur !== "layer" && (
                    <div className="ml-6 mb-1 rounded border border-slate-700 bg-slate-900/50 p-2 space-y-1" data-testid={`batch-map-${s.name}`}>
                      <div className="text-[10px] text-slate-500 uppercase">{t("Mapowanie kolumn")} → {t(TARGET_LABELS[cur])}</div>
                      {(MODULES[cur]?.fields || []).filter((fl) => fl.name !== "name" && fl.name !== "number").map((fl) => (
                        <div key={fl.name} className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-300 w-28 shrink-0 truncate">{t(fl.label)}</span>
                          <select value={batch[s.name]?.fieldMap?.[fl.name] || ""} data-testid={`batch-map-${s.name}-${fl.name}`}
                            onChange={(e) => setBatch((b) => ({ ...b, [s.name]: { ...b[s.name], fieldMap: { ...(b[s.name]?.fieldMap || {}), [fl.name]: e.target.value } } }))}
                            className="flex-1 bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-[11px]">
                            <option value="">— {t("pomiń")} —</option>
                            {(batch[s.name]?.columns || []).map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                  );
                })}
                <button onClick={doImportAll} disabled={busy} data-testid="import-all-shp"
                  className="w-full mt-1 bg-emerald-600 hover:bg-emerald-500 rounded py-1.5 text-xs disabled:opacity-60">{t("Importuj wszystkie zaznaczone")}</button>
              </div>
            </div>
          )}
          {previewCols.length > 0 && rows.length > 0 && (
            <div data-testid="import-preview" className="border-t border-slate-700 pt-3">
              <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1.5"><Table2 size={13} className="text-cyan-400" /> {t("Podgląd danych (pierwsze wiersze)")}</label>
              <div className="overflow-x-auto rounded border border-slate-700">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-900">
                    <tr>{previewCols.map((c) => <th key={c} className="text-left px-2 py-1.5 text-cyan-300 font-semibold whitespace-nowrap border-b border-slate-700">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, ri) => (
                      <tr key={ri} data-testid={`preview-row-${ri}`} className="odd:bg-slate-900/40">
                        {previewCols.map((c) => <td key={c} className="px-2 py-1 text-slate-300 whitespace-nowrap max-w-[160px] truncate">{String(r[c] ?? "")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {columns.length > previewCols.length && <p className="text-[11px] text-slate-500 mt-1">{t("Pokazano pierwsze 8 kolumn z")} {columns.length}.</p>}
            </div>
          )}
          {target !== "layer" && columns.length > 0 && (
            <div data-testid="import-field-map" className="border-t border-slate-700 pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-slate-400">{t("Mapowanie kolumn na pola modułu")}</label>
                <button onClick={saveTemplate} disabled={tplBusy} data-testid="import-save-template"
                  className="text-[11px] flex items-center gap-1 bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 disabled:opacity-60">
                  <Save size={12} /> {t("Zapisz jako szablon")}
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {(MODULES[target]?.fields || []).filter((fl) => fl.name !== "name" && fl.name !== "number").map((fl) => (
                  <div key={fl.name} className="flex items-center gap-2">
                    <span className="text-xs text-slate-300 w-32 shrink-0 truncate">{t(fl.label)}</span>
                    <select value={fieldMap[fl.name] || ""} data-testid={`map-${fl.name}`}
                      onChange={(e) => setFieldMap((m) => ({ ...m, [fl.name]: e.target.value }))}
                      className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs">
                      <option value="">— pomiń —</option>
                      {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div className="mt-3 border-t border-slate-700/60 pt-2" data-testid="import-custom-fields">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] text-slate-400 uppercase">{t("Własne pola (dowolne kolumny z pliku)")}</label>
                  <div className="flex gap-1.5">
                    <button onClick={addAllColumns} data-testid="import-add-all-cols"
                      className="text-[11px] bg-slate-700 hover:bg-slate-600 rounded px-2 py-1">{t("Dodaj wszystkie kolumny")}</button>
                    <button onClick={addCustomRow} data-testid="import-add-custom"
                      className="text-[11px] flex items-center gap-1 bg-slate-700 hover:bg-slate-600 rounded px-2 py-1"><Plus size={12} /> {t("Dodaj pole")}</button>
                  </div>
                </div>
                {customMaps.length === 0
                  ? <p className="text-[11px] text-slate-500">{t("Dodaj własne pola, aby zapisać dodatkowe kolumny z pliku (np. Relacja, Typ kabla, Długość).")}</p>
                  : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {customMaps.map((m, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`custom-row-${i}`}>
                          <input value={m.field} onChange={(e) => updCustom(i, { field: e.target.value })} placeholder={t("nazwa pola")} data-testid={`custom-field-${i}`}
                            className="w-32 shrink-0 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs" />
                          <span className="text-slate-500 text-xs shrink-0">←</span>
                          <select value={m.col} onChange={(e) => updCustom(i, { col: e.target.value })} data-testid={`custom-col-${i}`}
                            className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs">
                            <option value="">— {t("kolumna z pliku")} —</option>
                            {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <button onClick={() => rmCustom(i)} data-testid={`custom-rm-${i}`} className="text-red-400 hover:text-red-300 shrink-0"><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <p className="text-[11px] text-slate-500 mt-1">{t("Nazwa obiektu pobierana automatycznie z kolumny name/nazwa/numer. Wszystkie kolumny z pliku są też zawsze zapisywane w danych obiektu. Zapisany szablon wczyta się automatycznie przy kolejnym imporcie do tej warstwy.")}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-slate-600 hover:bg-slate-700">{t("Anuluj")}</button>
          <button onClick={doImport} disabled={busy} data-testid="import-submit"
            className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-60">{t("Importuj")}</button>
        </div>
      </div>
    </div>
  );
}
